import React, { useState, useEffect } from 'react';
import axios from 'axios';
import StockCard from './components/StockCard';
import StockDetail from './components/StockDetail';
import Dashboard from './components/Dashboard';
import AIDashboard from './components/AIDashboard';
import './App.css';

// Use production API URL if available, otherwise localhost for development
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
    ? 'http://localhost:5001/api' 
    : 'https://your-backend-url.com/api'); // Update this with your deployed backend URL

function App() {
  const [stocks, setStocks] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchSymbol, setSearchSymbol] = useState('');
  const [customSymbols, setCustomSymbols] = useState('AAPL,MSFT,GOOGL,AMZN,TSLA');
  const [view, setView] = useState('ai'); // 'ai', 'dashboard' or 'list'

  const fetchStocks = async (symbols = null, includeDetails = false, useMock = false) => {
    setLoading(true);
    setError(null);
    try {
      const symbolsParam = symbols || customSymbols;
      const params = { 
        symbols: symbolsParam,
        details: includeDetails
      };
      if (useMock) {
        params.mock = 'true';
      }
      const response = await axios.get(`${API_URL}/stocks`, { 
        params,
        timeout: 60000 // 1 minute timeout
      });
      setStocks(response.data);
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        setError('Request timed out. Try using mock data or fewer symbols.');
      } else if (err.code === 'ERR_NETWORK' || err.message.includes('ERR_EMPTY_RESPONSE')) {
        setError('Cannot connect to backend. Make sure the backend is running on port 5001.');
      } else {
        setError('Failed to fetch stock data. Make sure the backend is running and Yahoo Finance API is accessible.');
      }
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStockDetail = async (symbol) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/stocks/${symbol}`);
      setSelectedStock(response.data);
      setView('detail');
    } catch (err) {
      setError('Failed to fetch stock details.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Don't auto-fetch on load - only when user clicks refresh
  // useEffect(() => {
  //   fetchStocks();
  //   const interval = setInterval(() => {
  //     fetchStocks();
  //   }, 30000);
  //   return () => clearInterval(interval);
  // }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchSymbol.trim()) {
      fetchStockDetail(searchSymbol.trim());
    }
  };

  const handleCustomSymbols = (e) => {
    e.preventDefault();
    if (customSymbols.trim()) {
      fetchStocks(customSymbols.trim());
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>📊 Stock Mention Tracker</h1>
        <p>Track Reddit, Hacker News & News mentions • Analyze Hype vs Real • Predict price movements</p>
        {view === 'ai' && (
          <p style={{ fontSize: '1rem', marginTop: '10px', opacity: 0.95 }}>
            🤖 Focused on AI & LLM Companies • Model Announcements • Investment Timing
          </p>
        )}
      </header>

      <div className="controls">
        <div className="view-toggle">
          <button 
            className={`btn ${view === 'ai' ? 'btn-active' : 'btn-secondary'}`}
            onClick={() => { setView('ai'); setSelectedStock(null); }}
          >
            🤖 AI Companies
          </button>
          <button 
            className={`btn ${view === 'dashboard' ? 'btn-active' : 'btn-secondary'}`}
            onClick={() => { setView('dashboard'); setSelectedStock(null); }}
          >
            📈 Dashboard
          </button>
          <button 
            className={`btn ${view === 'list' ? 'btn-active' : 'btn-secondary'}`}
            onClick={() => { setView('list'); setSelectedStock(null); }}
          >
            📋 List View
          </button>
        </div>

        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={searchSymbol}
            onChange={(e) => setSearchSymbol(e.target.value.toUpperCase())}
            placeholder="Search by symbol (e.g., AAPL)"
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        <form onSubmit={handleCustomSymbols} className="search-form">
          <input
            type="text"
            value={customSymbols}
            onChange={(e) => setCustomSymbols(e.target.value)}
            placeholder="Multiple symbols (comma-separated)"
            className="search-input"
          />
          <button type="submit" className="btn btn-primary">Load Stocks</button>
        </form>

        <button onClick={() => fetchStocks()} className="btn btn-secondary">
          🔄 Refresh
        </button>
        <button onClick={() => fetchStocks(null, false, true)} className="btn btn-secondary">
          🎭 Use Mock Data
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {view === 'ai' ? (
        <AIDashboard />
      ) : loading && stocks.length === 0 && !selectedStock ? (
        <div className="loading">Loading stock data...</div>
      ) : selectedStock ? (
        <StockDetail 
          stock={selectedStock} 
          onBack={() => { setSelectedStock(null); setView('dashboard'); }}
          apiUrl={API_URL}
        />
      ) : view === 'dashboard' ? (
        <Dashboard 
          stocks={stocks} 
          onStockClick={fetchStockDetail}
          loading={loading}
        />
      ) : (
        <div className="stocks-grid">
          {stocks.map((stock) => (
            <StockCard
              key={stock.symbol}
              stock={stock}
              onClick={() => fetchStockDetail(stock.symbol)}
            />
          ))}
        </div>
      )}

      {!loading && stocks.length === 0 && !selectedStock && !error && (
        <div className="no-results">No stocks found. Try searching for a symbol.</div>
      )}
    </div>
  );
}

export default App;
